import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';

export const STATUS_CONFIG = {
  initiating: {
    title: 'Connecting to the card reader',
    subtitle: 'This will only take a moment…',
    showAmount: false
  },
  ready: {
    title: 'Please swipe, insert or tap your card',
    subtitle: 'Follow the prompts on the card reader.',
    manualSubtitle: 'Or enter your card details manually below.',
    showAmount: true
  },
  authorizing: {
    title: 'Authorizing your payment',
    subtitle: 'Hang tight — this only takes a few seconds.',
    showAmount: false
  },
  success: {
    title: 'Payment completed successfully',
    subtitle:
      "Your payment has been processed and your documents are ready. Select the documents you'd like to keep, choose how you'd like to receive them, then acknowledge and continue to the next step.",
    showAmount: false
  },
  canceled: {
    title: 'Payment canceled',
    subtitle: 'You can try again, or choose another way to pay.',
    showAmount: false
  },
  error: {
    title: "Payment didn't go through",
    subtitle: 'Please try again.',
    manualSubtitle: 'Please try again, or enter your card details manually.',
    showAmount: false
  }
};

export const SAVE_CARD_STATUS_CONFIG = {
  ...STATUS_CONFIG,
  ready: {
    title: 'Please swipe, insert or tap your card',
    subtitle: 'Your card is only saved for this authorization. Nothing is charged today.',
    showAmount: false
  },
  authorizing: {
    title: 'Saving your card',
    subtitle: 'Hang tight — this only takes a few seconds.',
    showAmount: false
  },
  success: { title: 'Your card is saved', subtitle: 'Nothing was charged.', showAmount: false },
  canceled: {
    title: 'Card not saved',
    subtitle: 'You can try again on the reader.',
    showAmount: false
  },
  error: {
    title: "We couldn't save your card",
    subtitle: 'Please try again, or ask us for help.',
    showAmount: false
  }
};

export const ReaderVisual = ({ status }) => {
  if (status === 'success')
    return (
      <div className="flex h-[150px] w-[150px] animate-check-bounce items-center justify-center rounded-full bg-gradient-to-br from-success-400 to-success-600 shadow-[0_18px_40px_-12px_rgba(9,146,80,0.6)] ring-8 ring-success-50">
        <Icon icon="check-thick-linear" color="white" size={72} />
      </div>
    );

  if (status === 'error' || status === 'canceled')
    return <Icon icon="illustration-payment-failed" size={172} className="animate-enter-up" />;

  if (status === 'authorizing')
    return (
      <>
        <Icon icon="illustration-kiosk-tap" size={300} className="relative" />
      </>
    );

  if (status === 'ready')
    return (
      <>
        <Icon icon="illustration-kiosk-tap" size={300} className="relative" />
      </>
    );

  // initiating
  return (
    <>
      <Icon icon="kiosk" size={300} className="relative" />
    </>
  );
};

export const ReaderStatus = ({
  status = 'initiating',
  amount,
  message,
  showManualEntry = false,
  statusConfig = STATUS_CONFIG
}) => {
  const config = statusConfig[status] || statusConfig.initiating;
  // Prefer the live terminal prompt for tap/error states.
  const title = (status === 'ready' || status === 'error') && message ? message : config.title;
  const subtitle = (showManualEntry && config.manualSubtitle) || config.subtitle;

  return (
    <div className="flex w-full flex-col items-center gap-2">
      {config.showAmount && amount && (
        <div className="flex flex-col items-center gap-2">
          <Typography variant="label" size="large" weight="medium" color="neutral-700">
            YOUR PORTION TODAY IS
          </Typography>
          <Typography variant="h3" weight="extrabold" color="primary-500" className="text-4xl">
            {amount}
          </Typography>
        </div>
      )}
      <div
        key={status}
        className="relative flex h-[300px] w-[359px] max-w-full items-center justify-end">
        <ReaderVisual status={status} />
      </div>
      <div
        key={`copy-${status}`}
        className="flex w-full max-w-[520px] animate-enter-up flex-col items-center gap-2 text-center">
        <Typography
          variant="p"
          size="large"
          weight="semibold"
          color="neutral-900"
          className="!text-lg !leading-7">
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="p"
            size="medium"
            color="neutral-700"
            className="!text-base !leading-6">
            {subtitle}
          </Typography>
        )}
      </div>
    </div>
  );
};
